import { useEffect, useRef, useState } from 'react';

type Mode = 'design' | 'html' | 'preview';

const BLOCKS = [{ v: 'p', t: 'Normal' }, { v: 'h1', t: 'Heading 1' }, { v: 'h2', t: 'Heading 2' }, { v: 'h3', t: 'Heading 3' }, { v: 'h4', t: 'Heading 4' }, { v: 'h5', t: 'Heading 5' }, { v: 'h6', t: 'Heading 6' }];
const FONTS = ['Times New Roman', 'Tahoma', 'Verdana', 'Arial', 'MS Sans Serif', 'Courier', 'Segoe UI'];
// execCommand fontSize is 1–7; labelled like the LIMS HtmlEditor ("1 (8pt)" …).
const SIZES = [{ v: '1', t: '1 (8pt)' }, { v: '2', t: '2 (10pt)' }, { v: '3', t: '3 (12pt)' }, { v: '4', t: '4 (14pt)' }, { v: '5', t: '5 (18pt)' }, { v: '6', t: '6 (24pt)' }, { v: '7', t: '7 (36pt)' }];

/**
 * Lightweight rich-text (HTML) editor for the Employee Resume tab — mirrors the
 * legacy XAF HtmlEditor: a formatting toolbar over a contentEditable Design area,
 * plus raw HTML and Preview modes (Design / HTML / Preview tabs at the bottom).
 * Stores HTML in Employee.Resume. Uses document.execCommand so it needs no extra
 * package dependency (CLAUDE.md §15).
 */
export function RichTextEditor({ value, onChange }: { value: string; onChange: (html: string) => void }) {
  const [mode, setMode] = useState<Mode>('design');
  const [block, setBlock] = useState('p');
  const [font, setFont] = useState('Times New Roman');
  const ref = useRef<HTMLDivElement>(null);
  // Clicking a toolbar <select> (font / size / block) or the colour input moves focus OUT of the
  // contentEditable, which collapses the text selection — so the command would apply to nothing.
  // We remember the last selection made inside the editor and restore it before running the command.
  const savedRange = useRef<Range | null>(null);

  // Sync the contentEditable only when the value differs from the live DOM, so
  // typing (which already updated the DOM) never resets the caret position.
  useEffect(() => {
    if (mode === 'design' && ref.current && ref.current.innerHTML !== value) ref.current.innerHTML = value;
  }, [value, mode]);

  // Track the last REAL (non-collapsed) selection inside the editor. A caret (collapsed) never
  // overwrites it, so clicking a toolbar <select> can't wipe the highlighted text we want to style.
  const saveSel = () => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount && !sel.isCollapsed && ref.current && ref.current.contains(sel.anchorNode)) {
      savedRange.current = sel.getRangeAt(0).cloneRange();
    }
  };
  // selectionchange fires for every selection update — the most reliable capture point.
  useEffect(() => {
    document.addEventListener('selectionchange', saveSel);
    return () => document.removeEventListener('selectionchange', saveSel);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const restoreSel = () => {
    const sel = window.getSelection();
    if (sel && savedRange.current) {
      try { sel.removeAllRanges(); sel.addRange(savedRange.current); } catch { /* range stale — ignore */ }
    }
  };
  const emit = () => { if (ref.current) onChange(ref.current.innerHTML); };
  // execCommand-based commands (bold/italic/lists/align/block/link) keep the selection.
  const runCmd = (cmd: string, arg?: string) => {
    ref.current?.focus();
    restoreSel();
    document.execCommand(cmd, false, arg);
    emit();
    saveSel();
  };
  const exec = (cmd: string, arg?: string) => runCmd(cmd, arg);   // buttons keep selection → immediate
  const execSel = (cmd: string, arg?: string) => { window.setTimeout(() => runCmd(cmd, arg), 0); };   // selects steal focus → defer
  // Inline style (font / size / colour). Uses execCommand with CSS output so it REPLACES the existing
  // formatting on the selection (no nested <span> build-up) instead of wrapping again each time.
  // Deferred a tick so the editor regains focus after the <select>/colour-picker; the selection is
  // restored first (tracked reliably via the selectionchange listener above).
  const runStyleCmd = (cmd: string, arg: string) => {
    const el = ref.current; if (!el) return;
    el.focus();
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0 || sel.isCollapsed || !el.contains(sel.anchorNode)) restoreSel();
    try { document.execCommand('styleWithCSS', false, 'true'); } catch { /* ignore */ }
    document.execCommand(cmd, false, arg);
    try { document.execCommand('styleWithCSS', false, 'false'); } catch { /* ignore */ }
    emit();
    saveSel();
  };
  const applyStyle = (cmd: string, arg: string) => { window.setTimeout(() => runStyleCmd(cmd, arg), 0); };
  const btn = (cmd: string, icon: string, title: string) => (
    <button type="button" className="rte-btn" title={title} onMouseDown={(e) => { e.preventDefault(); exec(cmd); }}><i className={`ti ${icon}`} /></button>
  );

  return (
    <div className="rte">
      {mode === 'design' && (
        <div className="rte-toolbar">
          <select className="rte-block" title="Paragraph format" value={block} onChange={(e) => { setBlock(e.target.value); execSel('formatBlock', `<${e.target.value}>`); }}>
            {BLOCKS.map((b) => <option key={b.v} value={b.v}>{b.t}</option>)}
          </select>
          <select className="rte-font" title="Font name" value={font} onChange={(e) => { const v = e.target.value; setFont(v); applyStyle('fontName', v); }}>
            {FONTS.map((f) => <option key={f} value={f} style={{ fontFamily: f }}>{f}</option>)}
          </select>
          <select className="rte-size" title="Font size" defaultValue="" onChange={(e) => { const v = e.target.value; e.currentTarget.selectedIndex = 0; applyStyle('fontSize', v); }}>
            <option value="" disabled>(Font Size)</option>
            {SIZES.map((s) => <option key={s.v} value={s.v}>{s.t}</option>)}
          </select>
          <input type="color" className="rte-color" title="Text colour" defaultValue="#000000" onChange={(e) => { const v = e.target.value; applyStyle('foreColor', v); }} />
          <span className="rte-sep" />
          {btn('bold', 'ti-bold', 'Bold')}
          {btn('italic', 'ti-italic', 'Italic')}
          {btn('underline', 'ti-underline', 'Underline')}
          {btn('strikeThrough', 'ti-strikethrough', 'Strikethrough')}
          <span className="rte-sep" />
          {btn('justifyLeft', 'ti-align-left', 'Align left')}
          {btn('justifyCenter', 'ti-align-center', 'Align center')}
          {btn('justifyRight', 'ti-align-right', 'Align right')}
          <span className="rte-sep" />
          {btn('insertUnorderedList', 'ti-list', 'Bullet list')}
          {btn('insertOrderedList', 'ti-list-numbers', 'Numbered list')}
          {btn('outdent', 'ti-indent-decrease', 'Outdent')}
          {btn('indent', 'ti-indent-increase', 'Indent')}
          <span className="rte-sep" />
          <button type="button" className="rte-btn" title="Insert link" onMouseDown={(e) => { e.preventDefault(); const url = window.prompt('Link URL:'); if (url) exec('createLink', url); }}><i className="ti ti-link" /></button>
          {btn('removeFormat', 'ti-clear-formatting', 'Clear formatting')}
        </div>
      )}

      {mode === 'design' && (
        <div ref={ref} className="rte-area" contentEditable suppressContentEditableWarning
          onInput={() => { emit(); saveSel(); }}
          onMouseUp={saveSel} onKeyUp={saveSel} />
      )}
      {mode === 'html' && <textarea className="rte-area rte-html" value={value} onChange={(e) => onChange(e.target.value)} />}
      {mode === 'preview' && <div className="rte-area rte-preview" dangerouslySetInnerHTML={{ __html: value }} />}

      <div className="rte-modes">
        {(['design', 'html', 'preview'] as Mode[]).map((m) => (
          <button key={m} type="button" className={`rte-mode${mode === m ? ' active' : ''}`} onClick={() => setMode(m)}>
            {m === 'design' ? 'Design' : m === 'html' ? 'HTML' : 'Preview'}
          </button>
        ))}
      </div>
    </div>
  );
}
